"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ExpressionEvaluator = void 0;
var expr_eval_1 = require("expr-eval");
var moment = require("moment");
var lodash_1 = require("lodash");
var ExpressionEvaluator = /** @class */ (function () {
    function ExpressionEvaluator() {
    }
    /**
     * Evaluates expression to any value
     * @param expression
     * @param context
     * @param defaultValue define value to be returned in case an exception is thrown by evaluator
     */
    ExpressionEvaluator.evaluateExpression = function (expression, context, defaultValue) {
        try {
            return this.evaluate(expression, context);
        }
        catch (e) {
            return defaultValue;
        }
    };
    /**
     * Evaluates boolean
     * @param expression
     * @param context
     */
    ExpressionEvaluator.evaluateBoolean = function (expression, context) {
        try {
            return this.evaluate(expression, context);
        }
        catch (e) {
            return false;
        }
    };
    /**
     * Evaluates template
     * @param template
     * @param context
     */
    ExpressionEvaluator.renderTemplate = function (template, context, escapeFields) {
        return template.replace(/\${([^}]+)\}/g, function (matched) {
            var field = matched.substring(2, matched.length - 1);
            var value = ExpressionEvaluator.evaluateExpression(field, context, "");
            return escapeFields ? (0, lodash_1.escape)(value) : value;
        });
    };
    /**
     * Evaluates expression, throws exceptions on failure
     * @param expression
     * @param context
     */
    ExpressionEvaluator.evaluate = function (expression, context) {
        var expr = this.getParser().parse(expression);
        return expr.evaluate(context);
    };
    /**
     * Create a parser instance with custom configs
     */
    ExpressionEvaluator.getParser = function () {
        if (!this.parserInstance) {
            this.parserInstance = new expr_eval_1.Parser({
                operators: {
                    'in': true
                }
            });
            // Add custom functions
            this.parserInstance.functions.parseFloat = this.parseFloat;
            this.parserInstance.functions.parseInt = this.parseInt;
            this.parserInstance.functions.parseDate = this.parseDate;
            this.parserInstance.functions.getDate = this.getDate;
            this.parserInstance.functions.changeDate = this.changeDate;
            this.parserInstance.functions.anyOf = this.anyOf;
            this.parserInstance.functions.getQueryParam = this.getQueryParam;
            this.parserInstance.functions.elementAt = this.elementAt;
            this.parserInstance.functions.isSet = this.isSet;
            this.parserInstance.functions.notEmpty = this.notEmpty;
            this.parserInstance.functions.capitalize = this.capitalize;
            this.parserInstance.functions.addArrayElement = this.addArrayElement;
            this.parserInstance.functions.removeArrayElement = this.removeArrayElement;
            this.parserInstance.functions.newArray = this.newArray;
            this.parserInstance.functions.newObject = this.newObject;
            this.parserInstance.functions.arrayLength = this.arrayLength;
            this.parserInstance.functions.lookupValue = this.lookupValue;
            this.parserInstance.functions.parseBoolean = this.parseBoolean;
            this.parserInstance.functions.formatDate = this.formatDate;
            this.parserInstance.functions.formatCurrency = this.formatCurrency;
            this.parserInstance.functions.formatDateUTC = this.formatDateUTC;
            this.parserInstance.functions.truncateArray = this.truncateArray;
        }
        return this.parserInstance;
    };
    /**
     * Parse value as float
     * @param value
     */
    ExpressionEvaluator.parseFloat = function (value) {
        if (value) {
            var floatVal = Number.parseFloat(value.toString());
            if (!isNaN(floatVal)) {
                return floatVal;
            }
        }
        return Number.MIN_VALUE;
    };
    /**
     * Parse value as int
     * @param value
     */
    ExpressionEvaluator.parseInt = function (value) {
        if (value) {
            var intVal = Number.parseInt(value.toString(), 10);
            if (!isNaN(intVal)) {
                return intVal;
            }
        }
        return Number.MIN_VALUE;
    };
    /**
     * Parse value as int
     * @param value
     */
    ExpressionEvaluator.parseDate = function (value) {
        if (value) {
            var date = moment(value);
            if (date.isValid()) {
                return date.toDate();
            }
        }
        return new Date(0);
    };
    /**
     * Format date
     * @param value
     * @param format
     */
    ExpressionEvaluator.formatDate = function (value, format) {
        return moment(ExpressionEvaluator.parseDate(value)).format(format);
    };
    /**
     * Format date
     * @param value
     * @param format
     */
    ExpressionEvaluator.formatDateUTC = function (value, format) {
        return moment.utc(ExpressionEvaluator.parseDate(value)).format(format);
    };
    /**
     * Gets the date adding the number of days specified to todays date. If no parameter is specified, todays date is returned
     * @param {number} value - number of days to add
     * @returns {daysToAdd}
     * @returns {monthsToAdd}
     * @returns {yearsToAdd}
     */
    ExpressionEvaluator.getDate = function (daysToAdd, monthsToAdd, yearsToAdd, removeTime) {
        var result = moment().utcOffset(0);
        if (daysToAdd) {
            result.add(daysToAdd, "days");
        }
        if (monthsToAdd) {
            result.add(monthsToAdd, "months");
        }
        if (yearsToAdd) {
            result.add(yearsToAdd, "years");
        }
        if (removeTime) {
            result = result.startOf("day");
        }
        return result.toDate();
    };
    /**
     * Gets the date adding the number of days specified to input date. If no parameter is specified, input date is returned
     * @param {Date} value - date to change
     * @param {number} value - number of days/month/year to add
     * @returns {daysToAdd}
     * @returns {monthsToAdd}
     * @returns {yearsToAdd}
     */
    ExpressionEvaluator.changeDate = function (dateToChange, daysToAdd, monthsToAdd, yearsToAdd) {
        var result = moment(dateToChange);
        if (daysToAdd) {
            result.add(daysToAdd, "days");
        }
        if (monthsToAdd) {
            result.add(monthsToAdd, "months");
        }
        if (yearsToAdd) {
            result.add(yearsToAdd, "years");
        }
        return result.toDate();
    };
    /**
     * Checks whether passed value is any of test values
     * @param value
     * @param testValues
     */
    ExpressionEvaluator.anyOf = function (value) {
        var testValues = [];
        for (var _i = 1; _i < arguments.length; _i++) {
            testValues[_i - 1] = arguments[_i];
        }
        for (var _a = 0, testValues_1 = testValues; _a < testValues_1.length; _a++) {
            var testValue = testValues_1[_a];
            if (value === testValue) {
                return true;
            }
        }
        return false;
    };
    ExpressionEvaluator.getQueryParam = function (value) {
        var result = new URLSearchParams(document.location.search).get(value);
        if (result) {
            return result;
        }
        return "";
    };
    /**
     * gets element/object of an array (or its property if necessary)
     * @param value
     * @param index
     * @param expression used to access property of arrays object/etc
     */
    ExpressionEvaluator.elementAt = function (value, index, dotNotation) {
        if (!value || (dotNotation && !value[index])) {
            return undefined;
        }
        if (dotNotation) {
            return ExpressionEvaluator.evaluateExpression(dotNotation, value[index], undefined);
        }
        return value[index];
    };
    ExpressionEvaluator.isSet = function (value) {
        return value !== null && value !== undefined;
    };
    ExpressionEvaluator.notEmpty = function (value) {
        return value.length > 0;
    };
    ExpressionEvaluator.capitalize = function (value) {
        if (value) {
            return value.replace(/(^|\s)\S/g, function (l) { return l.toUpperCase(); });
        }
        return value;
    };
    ExpressionEvaluator.addArrayElement = function (array, value) {
        array.push(value);
        return array;
    };
    ExpressionEvaluator.removeArrayElement = function (array, index) {
        if (!array || isNaN(index) || index < 0) {
            throw new Error("Invalid array index");
        }
        array.splice(index, 1);
        return array;
    };
    ExpressionEvaluator.arrayLength = function (array) {
        return array.length;
    };
    ExpressionEvaluator.newArray = function () {
        return [];
    };
    ExpressionEvaluator.newObject = function () {
        return {};
    };
    ExpressionEvaluator.lookupValue = function (id, lookups) {
        var item = lookups.find(function (x) { return x.id === id; });
        return item && item.value;
    };
    ExpressionEvaluator.truncateArray = function (array, start, end) {
        return array.slice(start, end);
    };
    ExpressionEvaluator.parseBoolean = function (value) {
        if (typeof value === "boolean") {
            return value;
        }
        if (typeof value === "string") {
            return value === "true";
        }
        return value !== null && value !== undefined;
    };
    ExpressionEvaluator.formatCurrency = function (value) {
        if (value) {
            return value
                .toString()
                .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        }
        return "";
    };
    return ExpressionEvaluator;
}());
exports.ExpressionEvaluator = ExpressionEvaluator;
//# sourceMappingURL=ExpressionEvaluator.js.map


//////////////////
// WEBPACK FOOTER
// ../node_modules/@athena-portal/journey-services/dist/ExpressionEvaluator.js
// module id = 268
// module chunks = 0